import React, { useMemo } from "react";
import Zoneperformance from "./Zoneperformance";
import html2canvas from "html2canvas";
import PptxGenJS from "pptxgenjs";
import TnBTable from "../../Components/Table/TrackingandBeatTable";
import PipelineTQPSection from "./TqpFunnel";
import PowerDeckFilter from "../../Components/Filters/PowerDeckFilter";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  digital_performace_table,
  funnel_data,
  funnel_table_data,
  gtme_performace_table,
  offtake_table_stateOffice,
  offtaketable,
  retail_table,
  retail_table_stateOffice,
} from "./Powerdeckkpi/Powerdeckapi.js";
import Zoneperformbystate from "./zoneperformbystate.jsx";
import { FaFileDownload } from "react-icons/fa";
import DigitalPipelineContainer from "./IlmcvPipeline";
import tatapowerdecklogo from "../../Artifacts/tatapowerdecklogo.png";
import tatapowerdecklogo2 from "../../Artifacts/tatapowerdecklogo2.png";

const IconActivity = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const IconTarget = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

// ─── Convert image import/URL to base64 for PptxGenJS ───────────────────────
const fetchImageAsBase64 = async (url) => {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// ─── Add logos at top + centered heading on every slide ─────────────────────
// Returns the newly created slide so callers can add content to it.
const addSlideWithHeader = async (
  pptx,
  headingText,
  logo1Base64,
  logo2Base64,
) => {
  const slide = pptx.addSlide();

  // Left logo
  if (logo1Base64) {
    slide.addImage({ data: logo1Base64, x: 0.15, y: 0.08, w: 1.3, h: 0.35 });
  }

  // Right logo
  if (logo2Base64) {
    slide.addImage({ data: logo2Base64, x: 8.9, y: 0.08, w: 0.9, h: 0.35 });
  }

  // Centered heading between the two logos
  slide.addText(headingText, {
    x: 1.4,
    y: 0.08,
    w: 7.2,
    h: 0.35,
    fontSize: 15,
    bold: true,
    align: "center",
    valign: "middle",
    color: "1F3864",
  });

  // Thin divider line below the header row
  slide.addShape(pptx.ShapeType.line, {
    x: 0.15,
    y: 0.48,
    w: 9.7,
    h: 0,
    line: { color: "234B63", width: 1.5 },
  });

  return slide;
};

// ─── extractReactTable with full rowSpan expansion ──────────────────────────
const extractReactTable = (tableId) => {
  const table = document.getElementById(tableId);
  if (!table) return null;

  const allTr = Array.from(table.querySelectorAll("tr"));
  if (allTr.length === 0) return null;

  const totalRows = allTr.length;
  const grid = Array.from({ length: totalRows }, () => ({}));
  const occupied = {};
  const key = (r, c) => `${r},${c}`;

  allTr.forEach((tr, rIdx) => {
    const cells = Array.from(tr.querySelectorAll("th, td"));
    let cIdx = 0;

    cells.forEach((cell) => {
      while (occupied[key(rIdx, cIdx)]) cIdx++;

      const isHeader = cell.tagName === "TH";
      const colSpan = Number(
        cell.getAttribute("data-colspan") || cell.colSpan || 1,
      );
      const rowSpan = Number(cell.rowSpan || 1);
      const text = (cell.innerText || "").trim();

      const isTotalRow = cell.parentElement?.innerText
        ?.toLowerCase()
        ?.includes("grand total");

      const makeCell = () => ({
        text,
        options: {
          colspan: colSpan,
          align: "center",
          valign: "middle",
          fontSize: 9,
          bold: isHeader || isTotalRow,
          fill: isHeader ? "234b63" : isTotalRow ? "DBEAFE" : "FFFFFF",
          color: isHeader ? "FFFFFF" : "000000",
        },
      });

      grid[rIdx][cIdx] = makeCell();

      for (let dr = 1; dr < rowSpan; dr++) {
        const targetRow = rIdx + dr;
        if (targetRow < totalRows) {
          grid[targetRow][cIdx] = makeCell();
        }
      }

      for (let dc = 0; dc < colSpan; dc++) {
        for (let dr = 1; dr < rowSpan; dr++) {
          occupied[key(rIdx + dr, cIdx + dc)] = true;
        }
      }

      cIdx += colSpan;
    });
  });

  return grid.map((sparseRow) => {
    const colIndices = Object.keys(sparseRow)
      .map(Number)
      .sort((a, b) => a - b);
    return colIndices.map((c) => sparseRow[c]);
  });
};

// ─── Paginated table slides with logos + heading on every page ──────────────
const addPaginatedTableSlidesWithHeader = async (
  pptx,
  title,
  rows,
  logo1,
  logo2,
  fontSize = 9,
  maxRowsPerSlide = 10,
) => {
  if (!rows || rows.length === 0) return;

  const headerRows = rows.filter((row) =>
    row.every((cell) => cell.options?.fill === "234b63"),
  );
  const dataRows = rows.filter(
    (row) => !row.every((cell) => cell.options?.fill === "234b63"),
  );

  const headers = headerRows.length > 0 ? headerRows : [rows[0]];
  const data = headerRows.length > 0 ? dataRows : rows.slice(1);
  const totalPages = Math.max(1, Math.ceil(data.length / maxRowsPerSlide));

  for (let page = 0; page < totalPages; page++) {
    const chunk = data.slice(
      page * maxRowsPerSlide,
      (page + 1) * maxRowsPerSlide,
    );
    const slideTitle =
      totalPages > 1 ? `${title} (${page + 1}/${totalPages})` : title;

    const slide = await addSlideWithHeader(pptx, slideTitle, logo1, logo2);

    slide.addTable([...headers, ...chunk], {
      x: 0.3,
      y: 0.7,
      w: 9.4,
      fontSize,
      border: { pt: 1, color: "D9D9D9" },
    });
  }
};

// ─── Digital pipeline slides with logos ─────────────────────────────────────
const addDigitalPipelineSlides = async (
  pptx,
  digital_performace,
  logo1,
  logo2,
  maxRowsPerSlide = 10,
) => {
  if (!digital_performace?.data) return;

  // const productLines = [
  //     ...new Set(digital_performace.data.map((r) => r.product_line)),
  // ].filter((pl) => pl !== "Others Digital Pipeline");
  /* ✅ Required sequence (same as UI) */
  const sequence = ["HCV Trucks", "ILMCV Trucks", "CV Passenger", "SCV & PU"];

  /* Get product lines */
  let productLines = [
    ...new Set(digital_performace.data.map((r) => r.product_line)),
  ].filter((pl) => pl !== "Others Digital Pipeline");

  /* ✅ Apply same sorting as UI */
  productLines = productLines.sort((a, b) => {
    const indexA = sequence.indexOf(a);
    const indexB = sequence.indexOf(b);

    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });

  let pendingTable = null;

  for (const pl of productLines) {
    const rows = extractReactTable(`digitalPipeline_${pl}`);
    if (!rows) continue;

    const headerRows = rows.filter((row) =>
      row.every((cell) => cell.options?.fill === "234b63"),
    );
    const dataRows = rows.filter(
      (row) => !row.every((cell) => cell.options?.fill === "234b63"),
    );

    const headers = headerRows.length > 0 ? headerRows : [rows[0]];
    const data = headerRows.length > 0 ? dataRows : rows.slice(1);

    // Large table → own paginated slides
    if (data.length > maxRowsPerSlide) {
      await addPaginatedTableSlidesWithHeader(
        pptx,
        `${pl} Digital Pipeline`,
        rows,
        logo1,
        logo2,
        9,
        maxRowsPerSlide,
      );
      continue;
    }

    // Small table → try to combine two per slide
    const tableObj = {
      title: `${pl} Digital Pipeline`,
      rows: [...headers, ...data],
    };

    if (!pendingTable) {
      pendingTable = tableObj;
    } else {
      const slide = await addSlideWithHeader(
        pptx,
        "Digital Pipeline",
        logo1,
        logo2,
      );

      // First table (top half)
      slide.addText(pendingTable.title, {
        x: 0.5,
        y: 0.65,
        fontSize: 12,
        bold: true,
      });
      slide.addTable(pendingTable.rows, {
        x: 0.3,
        y: 0.95,
        w: 9.4,
        fontSize: 9,
        border: { pt: 1, color: "D9D9D9" },
      });

      // Second table (bottom half)
      slide.addText(tableObj.title, {
        x: 0.5,
        y: 3.05,
        fontSize: 12,
        bold: true,
      });
      slide.addTable(tableObj.rows, {
        x: 0.3,
        y: 3.35,
        w: 9.4,
        fontSize: 9,
        border: { pt: 1, color: "D9D9D9" },
      });

      pendingTable = null;
    }
  }

  // Odd table left over
  if (pendingTable) {
    const slide = await addSlideWithHeader(
      pptx,
      pendingTable.title,
      logo1,
      logo2,
    );
    slide.addTable(pendingTable.rows, {
      x: 0.3,
      y: 0.7,
      w: 9.4,
      fontSize: 9,
      border: { pt: 1, color: "D9D9D9" },
    });
  }
};

// ─── Zone performance slide with logos ──────────────────────────────────────
const addZoneSlide = async (pptx, logo1, logo2) => {
  const rows = extractReactTable("zonePerformanceTable");
  if (!rows) return;
  const slide = await addSlideWithHeader(
    pptx,
    "Zone Performance - Retail & Offtake (By Product Line)",
    logo1,
    logo2,
  );
  slide.addTable(rows, {
    x: 0.3,
    y: 0.7,
    w: 9.4,
    fontSize: 9,
    border: { pt: 1, color: "D9D9D9" },
  });
};

// ─── Zone-state performance slides with logos ────────────────────────────────
const addZoneStateSlide = async (pptx, logo1, logo2, maxRowsPerSlide = 6) => {
  const rows = extractReactTable("zoneStatePerformanceTable");
  if (!rows) return;
  await addPaginatedTableSlidesWithHeader(
    pptx,
    "State Performance - Retail & Offtake",
    rows,
    logo1,
    logo2,
    8,
    maxRowsPerSlide,
  );
};

// ─── Pipeline summary combined slide (funnel + LOB) with logos ───────────────
const addPipelineSummaryCombinedSlide = async (pptx, logo1, logo2) => {
  const funnelElement = document.getElementById("tqpFunnelSection");
  const lobElement = document.getElementById("productLineTable");
  if (!funnelElement && !lobElement) return;

  await new Promise((r) => setTimeout(r, 300));

  const slide = await addSlideWithHeader(
    pptx,
    "Pipeline Summary",
    logo1,
    logo2,
  );

  if (funnelElement) {
    const funnelCanvas = await html2canvas(funnelElement, {
      scale: 1.3,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    slide.addImage({
      data: funnelCanvas.toDataURL("image/png"),
      x: 0.7,
      y: 0.7,
      w: 8.2,
      h: 2,
    });
  }

  if (lobElement) {
    const lobCanvas = await html2canvas(lobElement, {
      scale: 1.3,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    slide.addText("Pipeline Summary - LOB Breakdown", {
      x: 0.5,
      y: 2.85,
      fontSize: 14,
      bold: true,
    });
    slide.addImage({
      data: lobCanvas.toDataURL("image/png"),
      x: 0.7,
      y: 3.15,
      w: 8.2,
      h: 2,
    });
  }
};

// ─── TnB data builder ────────────────────────────────────────────────────────
const buildTnBRows = (gtme = {}) => {
  const api = gtme?.data || {};
  const getValue = (arr, key) => arr?.find((i) => i.key === key)?.percent ?? 0;
  return [
    {
      Parameter: "Beat plan adherence - Physical - Geo Tagging",
      Target: "80%",
      HCV: `${getValue(api?.Beat_Plan, "HCV Trucks").toFixed(2)}%`,
      ILMCV: `${getValue(api?.Beat_Plan, "ILMCV Trucks").toFixed(2)}%`,
      CVP: `${getValue(api?.Beat_Plan, "CV Passenger").toFixed(2)}%`,
    },
    {
      Parameter: "CPR",
      Target: "0",
      HCV: getValue(api?.CPR, "HCV Trucks").toFixed(0),
      ILMCV: getValue(api?.CPR, "ILMCV Trucks").toFixed(0),
      CVP: getValue(api?.CPR, "CV Passenger").toFixed(0),
    },
    {
      Parameter: "% Beat Plan to Retail",
      Target: "30%",
      HCV: `${getValue(api?.Beat_Plan, "HCV Trucks").toFixed(2)}%`,
      ILMCV: `${getValue(api?.Beat_Plan, "ILMCV Trucks").toFixed(2)}%`,
      CVP: `${getValue(api?.Beat_Plan, "CV Passenger").toFixed(2)}%`,
    },
    {
      Parameter: "BTL Adherence",
      Target: "80%",
      HCV: `${getValue(api?.BTL_Adherence, "HCV Trucks").toFixed(2)}%`,
      ILMCV: `${getValue(api?.BTL_Adherence, "ILMCV Trucks").toFixed(2)}%`,
      CVP: `${getValue(api?.BTL_Adherence, "CV Passenger").toFixed(2)}%`,
    },
    {
      Parameter: "TQP Velocity (C3/TQP 25th)",
      Target: "70%",
      HCV: `${getValue(api?.Tqp_Velocity, "HCV Trucks").toFixed(2)}%`,
      ILMCV: `${getValue(api?.Tqp_Velocity, "ILMCV Trucks").toFixed(2)}%`,
      CVP: `${getValue(api?.Tqp_Velocity, "CV Passenger").toFixed(2)}%`,
    },
    {
      Parameter: "Market Visibility",
      Target: "90%",
      HCV: `${getValue(api?.Market_Visiblity, "HCV Trucks").toFixed(2)}%`,
      ILMCV: `${getValue(api?.Market_Visiblity, "ILMCV Trucks").toFixed(2)}%`,
      CVP: `${getValue(api?.Market_Visiblity, "CV Passenger").toFixed(2)}%`,
    },
    {
      Parameter: "%PVIN Usage",
      Target: "80%",
      HCV: `${getValue(api?.Pvin, "HCV Trucks").toFixed(2)}%`,
      ILMCV: `${getValue(api?.Pvin, "ILMCV Trucks").toFixed(2)}%`,
      CVP: `${getValue(api?.Pvin, "CV Passenger").toFixed(2)}%`,
    },
  ];
};

// ─── Main Component ──────────────────────────────────────────────────────────
const Powerdeckindex = () => {
  const formatTable = (data) => {
    const rowsData = data?.data || data;
    if (!Array.isArray(rowsData) || rowsData.length === 0) return null;

    const headerRow = Object.keys(rowsData[0]).map((h) => ({
      text: String(h),
      options: {
        bold: true,
        fill: "234B63",
        color: "FFFFFF",
        align: "center",
        valign: "middle",
        fontSize: 10,
      },
    }));

    const dataRows = rowsData.map((row) =>
      Object.values(row).map((val) => {
        let value = val;
        if (typeof val === "object" && val !== null) {
          value = "value" in val ? val.value : JSON.stringify(val);
        }
        return {
          text: String(value ?? ""),
          options: { align: "center", valign: "middle", fontSize: 10 },
        };
      }),
    );

    return [headerRow, ...dataRows];
  };

  // Accepts logo1, logo2 so the header appears on every paginated page
  const addTableSlide = async (
    pptx,
    title,
    tableData,
    logo1,
    logo2,
    maxRowsPerSlide = 10,
  ) => {
    const formatted = formatTable(tableData);
    if (!Array.isArray(formatted) || formatted.length === 0) return;

    const headerRow = formatted[0];
    const dataRows = formatted.slice(1);

    for (let i = 0; i < dataRows.length; i += maxRowsPerSlide) {
      const slideTitle = i === 0 ? title : `${title} (Contd.)`;
      const slide = await addSlideWithHeader(pptx, slideTitle, logo1, logo2);
      slide.addTable([headerRow, ...dataRows.slice(i, i + maxRowsPerSlide)], {
        x: 0.5,
        y: 0.75,
        w: 9,
        border: { type: "solid", pt: 1, color: "D9D9D9" },
        fontSize: 10,
        align: "center",
        valign: "middle",
      });
    }
  };

  // ─── Redux + API queries ─────────────────────────────────────────────────
  const { data, submitted } = useSelector((e) => e.filtersPowerDeck);

  const { data: funneldata } = useQuery({
    queryKey: ["funneldata", submitted],
    queryFn: () => funnel_data(data),
    initialData: [],
  });
  const { data: funneltabledata } = useQuery({
    queryKey: ["funneltabledata", submitted],
    queryFn: () => funnel_table_data(data),
    initialData: [],
  });
  const { data: retailtabledata } = useQuery({
    queryKey: ["retailtable", submitted],
    queryFn: () => retail_table(data),
    initialData: [],
  });
  const { data: offtakedata } = useQuery({
    queryKey: ["offtakedata", submitted],
    queryFn: () => offtaketable(data),
    initialData: [],
  });
  const { data: retailstate } = useQuery({
    queryKey: ["retailstate", submitted],
    queryFn: () => retail_table_stateOffice(data),
    initialData: [],
  });
  const { data: offtakestate } = useQuery({
    queryKey: ["offtakestate", submitted],
    queryFn: () => offtake_table_stateOffice(data),
    initialData: [],
  });
  const { data: gtme_perf } = useQuery({
    queryKey: ["gtme_performace", submitted],
    queryFn: () => gtme_performace_table(data),
    initialData: [],
  });
  const { data: digital_performace } = useQuery({
    queryKey: ["digital_performace", submitted],
    queryFn: () => digital_performace_table(data),
    initialData: [],
  });

  const tnbPPTData = useMemo(() => buildTnBRows(gtme_perf), [gtme_perf]);

  // ─── Main PPT export ─────────────────────────────────────────────────────
  const downloadPPT = async () => {
    const pptx = new PptxGenJS();

    // Pre-fetch both logos once — reused on every slide
    const [logo1, logo2] = await Promise.all([
      fetchImageAsBase64(tatapowerdecklogo),
      fetchImageAsBase64(tatapowerdecklogo2),
    ]);

    await addZoneSlide(pptx, logo1, logo2);
    await addZoneStateSlide(pptx, logo1, logo2, 6);
    await addPipelineSummaryCombinedSlide(pptx, logo1, logo2);
    await addDigitalPipelineSlides(pptx, digital_performace, logo1, logo2, 13);
    await addTableSlide(
      pptx,
      "GTME Parameter's Performance",
      tnbPPTData,
      logo1,
      logo2,
    );

    pptx.writeFile("PowerDeck_Report.pptx");
  };

  return (
    <div>
      <div className="md:flex w-full items-center mb-3 px-3 gap-3 pt-2">
        <div className="flex-1 border border-[#2c8a8b] rounded-md w-[350px] ">
          <PowerDeckFilter />
        </div>

        <div className="flex md:justify-end mt-2x">
          <button
            onClick={downloadPPT}
            className="px-4 py-2 bg-[#2A5775] text-white rounded whitespace-nowrap flex items-center gap-2"
          >
            Export PPT
            <FaFileDownload className="text-lg" />
          </button>
        </div>
      </div>

      <Zoneperformance
        databylob={retailtabledata}
        offtakedata={offtakedata}
        retailstate={retailstate}
        offtakestate={offtakestate}
      />
      <Zoneperformbystate
        retailstate={retailstate}
        offtakestate={offtakestate}
      />
      <PipelineTQPSection data={funneldata} tabledata={funneltabledata} />

      <div className="bg-white m-1 rounded-xl shadow-sm border border-slate-200/80 overflow-hidden mb-4 transition-all duration-300 hover:shadow-md">
        <div className="flex mt-2 p-2">
          <div className="p-1.5 bg-[#f0f6fa] rounded-lg text-[#245c81]">
            <IconActivity />
          </div>
          <h2 className="text-[17px] ml-2 mb-4 font-bold text-slate-800 tracking-tight">
            Digital Pipeline performance
          </h2>
        </div>
        <DigitalPipelineContainer digital_performace={digital_performace} />
      </div>

      <div className="bg-white m-1 rounded-xl shadow-sm border border-slate-200/80 overflow-hidden mb-4 transition-all duration-300 hover:shadow-md">
        <div className="flex mt-2 p-2">
          <div className="p-1.5 bg-[#f0f6fa] rounded-lg text-[#245c81]">
            <IconTarget />
          </div>
          <h2 className="text-[17px] ml-2 mb-4 font-bold text-slate-800 tracking-tight">
            GTME Parameter's Performance (Trucks & Buses)
          </h2>
        </div>
        <TnBTable gtme_performace={gtme_perf} />
      </div>
    </div>
  );
};

export default Powerdeckindex;
